import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMainSession } from '../services/chatApi';
import type { ChatSession } from '../services/chatApi';

/** Query cache key for the landing-session resolve-or-create. */
export const MAIN_SESSION_KEY = ['chat', 'main'] as const;

/**
 * Resolves the user's main (landing) session — GET /v1/chat/sessions/main,
 * server-side resolve-or-create (spec §6.1).
 *
 * On success the row is seeded into the ['chat-sessions','active'] list cache
 * (prepended when absent) so the sidebar and the current-session lookup can
 * see it before the list refetch lands. Selection itself is useChatSessions'
 * job: it prefers this id whenever the caller passes it in.
 */
export function useMainSession() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: MAIN_SESSION_KEY,
    queryFn: getMainSession,
    // A failed resolve should fall back to the latest session promptly;
    // the next tab-open remount refetches and retries the resolve.
    retry: false,
  });

  useEffect(() => {
    const main = query.data;
    if (!main) return;
    qc.setQueryData<ChatSession[]>(['chat-sessions', 'active'], (old) => {
      if (!old) return [main];
      if (old.some((x) => x.id === main.id)) return old;
      return [main, ...old];
    });
    // Seed-overwrite race (T6 review): a list fetch already in flight when the
    // seed landed can resolve without the main row and evict it from the
    // cache. Invalidate so the observed list refetches with the main row
    // included — the seed is a bridge, never the final state.
    qc.invalidateQueries({ queryKey: ['chat-sessions', 'active'] });
  }, [query.data, qc]);

  return query;
}
